import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, RefreshCcw, Send, UserPlus, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useConfirm } from '../../components/ConfirmDialog';
import { EmptyState, ErrorState, TableSkeleton, StatusPill, money, fmtDate, plural } from '../../components/ui';
import { Notice } from '../Financials/financeKit';

// Invoices › QuickBooks: send invoices to QuickBooks Online and add clients as customers, the way
// the Xero tab does: preview, tick, send, never twice. Backend: routes/qbSync.js.

const GREEN = 'bg-[#2CA01C]';
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export default function QuickBooksInvoicesTab() {
  const [view, setView] = useState(() => {
    const v = new URLSearchParams(window.location.search).get('view');
    return ['clients', 'settings'].includes(v) ? v : 'invoices';
  });
  const [status, setStatus] = useState(null);
  useEffect(() => { api.get('/qb-sync/status').then((r) => setStatus(r.data)).catch(() => setStatus({ connected: false })); }, []);
  if (!status) return <div className="card"><TableSkeleton rows={4} cols={5} /></div>;
  if (!status.connected) {
    return (
      <div className="card p-6">
        <EmptyState icon={Send} title="QuickBooks is not connected" hint="Connect QuickBooks to send invoices to it."
          action={<Link to="/settings?tab=integrations" className="btn-primary btn-sm">Go to integrations</Link>} />
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" role="tablist" aria-label="QuickBooks">
        {[['invoices', 'Invoices'], ['clients', 'Clients'], ['settings', 'Settings']].map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={view === id} onClick={() => setView(id)}
            className={`px-3 py-1.5 text-sm rounded-md font-medium ${view === id ? `${GREEN} text-white` : 'text-slate-600 hover:bg-slate-50'}`}>{label}</button>
        ))}
      </div>
      {view === 'invoices' && <InvoicesView company={status.companyName} onSettings={() => setView('settings')} />}
      {view === 'clients' && <ClientsView />}
      {view === 'settings' && <SettingsView />}
    </div>
  );
}

// ── Invoices ─────────────────────────────────────────────────────────────────
const INV_STATUS = { ready: { tone: 'success', label: 'Ready' }, in_qb: { tone: 'info', label: 'In QuickBooks' }, blocked: { tone: 'warning', label: 'Needs attention' } };

function InvoicesView({ company, onSettings }) {
  const confirm = useConfirm();
  const today = new Date();
  const [from, setFrom] = useState(ymd(new Date(today.getFullYear(), today.getMonth() - 1, 1)));
  const [to, setTo] = useState(ymd(today));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data: d } = await api.get(`/qb-sync/invoices/preview?from=${from}&to=${to}`);
      setData(d);
      setPicked(new Set(d.invoices.filter((i) => i.status === 'ready').map((i) => i.id)));
    } catch (e) { setError(e.response?.data?.message || 'Could not prepare the invoices.'); }
    finally { setLoading(false); }
  }, [from, to]);
  useEffect(() => { load(); }, [load]);

  const rows = data?.invoices || [];
  const ready = rows.filter((r) => r.status === 'ready');
  const chosen = ready.filter((r) => picked.has(r.id));
  const allPicked = ready.length > 0 && chosen.length === ready.length;
  const toggle = (id) => setPicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const noItem = data && !data.settings?.itemId;

  const send = async () => {
    const total = chosen.reduce((s, r) => s + r.total, 0);
    const ok = await confirm(`${plural(chosen.length, 'invoice')} (${money(total)}) go to ${company || 'QuickBooks'}. A bill-to that is not a QuickBooks customer yet is added first. An invoice already in QuickBooks is never sent again.`,
      { title: `Send ${plural(chosen.length, 'invoice')} to QuickBooks?`, confirmLabel: 'Send to QuickBooks' });
    if (!ok) return;
    setBusy(true);
    try {
      const { data: r } = await api.post('/qb-sync/invoices/push', { invoiceIds: chosen.map((x) => x.id) });
      const bad = r.results.filter((x) => !x.ok);
      const already = r.results.filter((x) => x.existing);
      if (r.added) toast.success(`${plural(r.added, 'invoice')} sent to QuickBooks`);
      if (already.length) toast.error(`Already in QuickBooks, not sent again: ${already.map((x) => x.name).join(', ')}`, { duration: 9000 });
      if (bad.length) toast.error(`${bad[0].name}: ${bad[0].error}${bad.length > 1 ? ` (+${bad.length - 1} more)` : ''}`, { duration: 9000 });
      await load();
    } catch (e) { toast.error(e.response?.data?.message || 'Sending to QuickBooks failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="card p-5 sm:p-6 space-y-4">
        <div>
          <h3 className="text-base font-semibold text-slate-900">Send invoices to QuickBooks</h3>
          <p className="text-sm text-slate-600 mt-1 max-w-[72ch]">
            Each Rostery invoice becomes a QuickBooks invoice for {company || 'your company'}, line for line, with the same number and total.
            Plan-managed invoices go to the plan manager and name the participant.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3 pt-3 border-t border-slate-100">
          <div><label htmlFor="qbi-from" className="text-[13px] text-slate-700 font-medium block mb-1">Invoice date from</label><input id="qbi-from" type="date" className="input-field" value={from} max={to} onChange={(e) => setFrom(e.target.value)} /></div>
          <div><label htmlFor="qbi-to" className="text-[13px] text-slate-700 font-medium block mb-1">To</label><input id="qbi-to" type="date" className="input-field" value={to} min={from} onChange={(e) => setTo(e.target.value)} /></div>
          <button type="button" className="btn-secondary" onClick={load} disabled={loading}>
            {loading ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <RefreshCcw size={14} aria-hidden="true" />} Show invoices
          </button>
        </div>
      </div>
      {noItem && (
        <Notice tone="amber" title="Choose the QuickBooks product/service first" actions={<button type="button" className="btn-secondary btn-sm" onClick={onSettings}>Open settings</button>}>
          QuickBooks needs a product or service on every invoice line. Pick the one Rostery should use (for example "Disability support"), and the GST-free tax code.
        </Notice>
      )}
      <div className="card overflow-hidden">
        {loading && !data ? <TableSkeleton rows={5} cols={6} />
          : error ? <ErrorState message={error} onRetry={load} />
          : !rows.length ? <EmptyState icon={Send} title="No invoices in these dates" hint="Choose other dates." />
          : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-[12px] text-slate-500">
                    <tr>
                      <th className="px-4 py-2.5 w-10"><input type="checkbox" aria-label="Select every invoice ready to send" disabled={!ready.length} checked={allPicked} onChange={() => setPicked(allPicked ? new Set() : new Set(ready.map((r) => r.id)))} /></th>
                      <th className="px-3 py-2.5 font-medium">Invoice</th>
                      <th className="px-3 py-2.5 font-medium">Client</th>
                      <th className="px-3 py-2.5 font-medium">Bill to</th>
                      <th className="px-2 py-2.5 font-medium text-right">Total</th>
                      <th className="px-3 py-2.5 font-medium">QuickBooks</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {rows.map((r) => {
                      const st = INV_STATUS[r.status] || INV_STATUS.ready;
                      return (
                        <tr key={r.id} className="align-top">
                          <td className="px-4 py-3"><input type="checkbox" aria-label={`Send ${r.invoiceNumber}`} disabled={r.status !== 'ready'} checked={picked.has(r.id)} onChange={() => toggle(r.id)} /></td>
                          <td className="px-3 py-3"><p className="font-medium text-slate-900">{r.invoiceNumber}</p><p className="text-[12px] text-slate-500">{fmtDate(r.date)}</p></td>
                          <td className="px-3 py-3 text-slate-700">{r.client}</td>
                          <td className="px-3 py-3 text-slate-700">{r.billTo || '—'}{['create', 'create_pm'].includes(r.customerHow) && r.status === 'ready' && <p className="text-[12px] text-slate-500">New customer, added when sent</p>}</td>
                          <td className="px-2 py-3 text-right tabular-nums text-slate-700">{money(r.total)}</td>
                          <td className="px-3 py-3"><StatusPill tone={st.tone} label={st.label} />{r.errors?.map((e) => <p key={e} className="text-[12px] text-amber-700 mt-1 max-w-[18rem]">{e}</p>)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-t border-slate-100 bg-slate-50/50">
                <p className="text-sm text-slate-600 flex-1">{plural(ready.length, 'invoice')} ready · {rows.filter((r) => r.status === 'in_qb').length} already in QuickBooks.</p>
                <button type="button" className="btn-primary" disabled={!chosen.length || busy} onClick={send}>
                  {busy ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Send size={14} aria-hidden="true" />} Send {plural(chosen.length, 'invoice')} to QuickBooks
                </button>
              </div>
            </>
          )}
      </div>
    </div>
  );
}

// ── Clients → Customers ──────────────────────────────────────────────────────
const CL_STATUS = { ready: { tone: 'success', label: 'Ready to add' }, in_qb: { tone: 'info', label: 'In QuickBooks' }, missing_name: { tone: 'warning', label: 'Name incomplete' } };
const TYPE_LABEL = { ndis_managed: 'NDIS', ndia_managed: 'NDIA-managed', plan_managed: 'Plan-managed', self_managed: 'Self-managed', aged_care: 'Aged care', support_coordination: 'Support coordination' };

function ClientsView() {
  const confirm = useConfirm();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setError(null);
    try { const { data: d } = await api.get('/qb-sync/clients'); setData(d); setPicked(new Set()); }
    catch (e) { setError(e.response?.data?.message || 'Could not read your QuickBooks customers.'); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const clients = data?.clients || [];
  const ready = clients.filter((c) => c.status === 'ready');
  const chosen = ready.filter((c) => picked.has(c.clientId));
  const allPicked = ready.length > 0 && chosen.length === ready.length;
  const toggle = (id) => setPicked((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const add = async (list) => {
    if (!list.length) return;
    const ok = await confirm(`${plural(list.length, 'customer')} will be created in QuickBooks with each client's name, email, mobile, address and NDIS number (in the notes).`,
      { title: `Add ${plural(list.length, 'client')} to QuickBooks?`, confirmLabel: 'Add to QuickBooks' });
    if (!ok) return;
    setBusy(true);
    try {
      const { data: r } = await api.post('/qb-sync/clients/add', { clientIds: list.map((c) => c.clientId) });
      const bad = r.results.filter((x) => !x.ok);
      const already = r.results.filter((x) => x.existing);
      if (r.added) toast.success(`${plural(r.added, 'client')} added to QuickBooks`);
      if (already.length) toast.error(`Already in QuickBooks, not created again: ${already.map((x) => x.name).join(', ')}`, { duration: 9000 });
      if (bad.length) toast.error(`${bad[0].name}: ${bad[0].error}${bad.length > 1 ? ` (+${bad.length - 1} more)` : ''}`, { duration: 9000 });
      await load();
    } catch (e) { toast.error(e.response?.data?.message || 'Adding clients to QuickBooks failed'); }
    finally { setBusy(false); }
  };

  if (error) return <div className="card"><ErrorState message={error} onRetry={load} /></div>;
  if (!data) return <div className="card"><TableSkeleton rows={5} cols={4} /></div>;
  return (
    <div className="space-y-4">
      <div className="card p-5 sm:p-6 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <h3 className="text-base font-semibold text-slate-900">Clients in QuickBooks</h3>
          <button type="button" className="btn-primary" disabled={!ready.length || busy} onClick={() => add(ready)}>
            {busy ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <UserPlus size={14} aria-hidden="true" />}
            {ready.length ? `Add all ${plural(ready.length, 'ready client')} to QuickBooks` : 'Every client is in QuickBooks'}
          </button>
        </div>
        <p className="text-sm text-slate-600 max-w-[72ch]">Invoices need each client as a QuickBooks customer. A client already there (same name or email) is linked, never added twice.</p>
      </div>
      <div className="card overflow-hidden">
        {!clients.length ? <EmptyState icon={UserPlus} title="No active clients" hint="Clients appear here once they are in Rostery." /> : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-[12px] text-slate-500">
                  <tr>
                    <th className="px-4 py-2.5 w-10"><input type="checkbox" aria-label="Select every client ready to add" disabled={!ready.length} checked={allPicked} onChange={() => setPicked(allPicked ? new Set() : new Set(ready.map((c) => c.clientId)))} /></th>
                    <th className="px-3 py-2.5 font-medium">Client</th><th className="px-3 py-2.5 font-medium">Type</th><th className="px-3 py-2.5 font-medium">NDIS number</th><th className="px-3 py-2.5 font-medium">QuickBooks</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {clients.map((c) => {
                    const st = CL_STATUS[c.status] || CL_STATUS.in_qb;
                    return (
                      <tr key={c.clientId} className="align-top">
                        <td className="px-4 py-3"><input type="checkbox" aria-label={`Add ${c.name}`} disabled={c.status !== 'ready'} checked={picked.has(c.clientId)} onChange={() => toggle(c.clientId)} /></td>
                        <td className="px-3 py-3"><p className="font-medium text-slate-900">{c.name}</p>{c.email && <p className="text-[12px] text-slate-500 break-all">{c.email}</p>}</td>
                        <td className="px-3 py-3 text-slate-700">{TYPE_LABEL[c.type] || c.type || '—'}</td>
                        <td className="px-3 py-3 text-slate-700 tabular-nums">{c.ndis || '—'}</td>
                        <td className="px-3 py-3">
                          <StatusPill tone={st.tone} label={st.label} />
                          {c.qbName && c.qbName !== c.name && <p className="text-[12px] text-slate-500 mt-1">As {c.qbName}</p>}
                          {c.reason && <p className="text-[12px] text-slate-500 mt-1 max-w-[18rem]">{c.reason}</p>}
                          {c.note && <p className="text-[12px] text-slate-500 mt-1 max-w-[18rem]">{c.note}</p>}
                          {c.status === 'missing_name' && <Link to={`/clients/${c.clientId}`} className="text-[12px] text-emerald-700 font-medium hover:underline">Fix in their profile</Link>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-t border-slate-100 bg-slate-50/50">
              <p className="text-sm text-slate-600 flex-1">{plural(clients.filter((c) => c.status === 'in_qb').length, 'client')} in QuickBooks · {ready.length} ready to add.</p>
              <button type="button" className="btn-primary" disabled={!chosen.length || busy} onClick={() => add(chosen)}>
                {busy ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <UserPlus size={14} aria-hidden="true" />} Add {plural(chosen.length, 'client')} to QuickBooks
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Settings: the product/service and tax codes invoice lines use ────────────
function SettingsView() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setError(null);
    try { const { data: d } = await api.get('/qb-sync/setup'); setData(d); setForm(d.settings || {}); }
    catch (e) { setError(e.response?.data?.message || 'Could not read your QuickBooks set-up.'); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    setBusy(true);
    try { await api.put('/qb-sync/settings', form); toast.success('QuickBooks settings saved'); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not save'); }
    finally { setBusy(false); }
  };
  if (error) return <div className="card"><ErrorState message={error} onRetry={load} /></div>;
  if (!data) return <div className="card"><TableSkeleton rows={3} cols={2} /></div>;
  const sel = (key, label, options, hint, blank) => (
    <div>
      <label htmlFor={`qbs-${key}`} className="text-[13px] text-slate-700 font-medium block mb-1">{label}</label>
      <select id={`qbs-${key}`} className="input-field" value={form[key] || ''} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value || null }))}>
        <option value="">{blank}</option>
        {options.map((o) => <option key={o.id} value={o.id}>{o.name}{o.description ? ` — ${o.description}` : ''}</option>)}
      </select>
      {hint && <p className="text-[12px] text-slate-500 mt-1">{hint}</p>}
    </div>
  );
  return (
    <div className="card p-5 sm:p-6 space-y-4 max-w-2xl">
      <h3 className="text-base font-semibold text-slate-900">QuickBooks settings</h3>
      {!data.items.length && <Notice tone="amber" title="No products or services in QuickBooks">Add a service item in QuickBooks (Sales › Products and services), for example "Disability support", then come back.</Notice>}
      {sel('itemId', 'Product/service for invoice lines', data.items, 'Every invoice line uses it; the line description carries the NDIS support item and date.', 'Choose…')}
      {sel('taxFree', 'Tax code for GST-free invoices', data.taxCodes, 'NDIS supports are GST-free: usually "GST free" (FRE).', data.taxCodes.length ? 'Choose…' : 'Your company does not track GST')}
      {sel('taxGst', 'Tax code for invoices with GST', data.taxCodes, 'Used when a Rostery invoice includes GST.', data.taxCodes.length ? 'Choose…' : 'Your company does not track GST')}
      {sel('timeItemId', 'Service on timesheets (optional)', data.items, 'Shown on each time activity Rostery sends.', 'None')}
      <div className="flex justify-end">
        <button type="button" className="btn-primary" disabled={busy} onClick={save}>{busy ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Save size={14} aria-hidden="true" />} Save</button>
      </div>
    </div>
  );
}
